import { useRef } from 'react';
import type { ChangeEvent, SyntheticEvent } from 'react';
import { editorCommands, type EditorCommands } from '../editor/index.jsx';
import { OfflineQueuedError, uploadAttachment } from './attachments.js';
import './features.css';
import { Icon } from '../mo/Icon.js';

/**
 * Mobile editing toolbar. In-flow (NOT position:fixed): the shell mounts it
 * at the bottom of .main-pane only while the iOS keyboard is open, and the
 * VisualViewport-driven --app-height keeps it sitting right above the
 * keyboard.
 */

// '\n| ' = 3 chars -> cursor inside the first header cell.
const TABLE_SNIPPET = '\n|  |  |\n| --- | --- |\n|  |  |\n';
// '```\n' = 4 chars -> cursor on the blank line inside the fence.
const CODE_SNIPPET = '```\n\n```';

export function ShortcutRow() {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const disabled = editorCommands() === null;

  /** Re-check at tap time — registration is imperative, not reactive. */
  const run = (fn: (c: EditorCommands) => void) => () => {
    const c = editorCommands();
    if (c) fn(c);
  };

  /** Keep the editor focused (and the keyboard up) when tapping a button. */
  const keepFocus = (e: SyntheticEvent) => e.preventDefault();

  const onPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let path: string;
    try {
      path = await uploadAttachment(file);
    } catch (err) {
      if (err instanceof OfflineQueuedError) {
        // Embed the provisional path now; the queue uploads it when back online.
        path = err.provisionalPath;
      } else {
        console.warn('[shortcut-row] photo upload failed', err);
        return;
      }
    }
    editorCommands()?.insertText(`![[${path}]]`);
  };

  const buttons: Array<{ label: string; title: string; onTap: () => void }> = [
    { label: '#', title: 'Heading', onTap: run((c) => c.insertText('# ')) },
    { label: '[[', title: 'Wikilink', onTap: run((c) => c.insertText('[[]]', 2)) },
    { label: '**', title: 'Bold', onTap: run((c) => c.wrapSelection('**', '**')) },
    { label: '```', title: 'Code block', onTap: run((c) => c.insertText(CODE_SNIPPET, 4)) },
    { label: '- [ ]', title: 'Todo', onTap: run((c) => c.toggleCheckbox()) },
    { label: '|', title: 'Table', onTap: run((c) => c.insertText(TABLE_SNIPPET, 3)) },
    { label: '↩', title: 'Undo', onTap: run((c) => c.undo()) },
    { label: '↪', title: 'Redo', onTap: run((c) => c.redo()) },
  ];

  return (
    <div className="shortcut-row" role="toolbar" aria-label="Editing shortcuts">
      {buttons.map((b) => (
        <button
          key={b.title}
          type="button"
          className="shortcut-btn"
          title={b.title}
          aria-label={b.title}
          disabled={disabled}
          onMouseDown={keepFocus}
          onClick={b.onTap}
        >
          {b.label}
        </button>
      ))}
      <button
        type="button"
        className="shortcut-btn"
        title="Photo"
        aria-label="Insert photo"
        disabled={disabled}
        onMouseDown={keepFocus}
        onClick={() => fileRef.current?.click()}
      >
        <Icon name="camera" size={15} />
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => void onPhoto(e)}
      />
    </div>
  );
}
